'use client';
import React, { useCallback, useRef, useEffect, useState, useMemo } from 'react';

import Box from '@mui/material/Box';
import { Descendant, Transforms, Editor, Text } from 'slate';
import { Editable, Slate, ReactEditor } from 'slate-react';

import { EditorContainer } from './components/EditorContainer';
import { SuggestionMenu } from './components/SuggestionMenu';
import { useEditorHistory } from './hooks/useEditorHistory';
import { useEditorValue } from './hooks/useEditorValue';
import { useRenderers } from './hooks/useRenderers';
import { useSlateEditor } from './hooks/useSlateEditor';
import { useSuggestions } from './hooks/useSuggestions';
import { fireImperitiveHandlers } from './normalizer/imperitiveHandlers';
import { useDecorate } from './plugins';
import { CustomElement, CustomText, FieldOption, ValueOption } from './types';
import { isFilterComplete, serializeQueryElements } from './utils';

interface QueryBuilderProps {
  errorMap?: Record<string, string>;
  externalQueryString?: string;
  fieldOptions: FieldOption[];
  getOperatorSuggestions?: (fieldName: string) => string[];
  isError?: boolean;
  isInitialized?: boolean;
  isLoadingFieldOptions?: boolean;
  isLoadingValueSuggestions?: boolean;
  onChange?: (value: string | undefined) => void;
  placeholder?: string;
  prioritizedCloudFields?: Set<string>;
  updateFieldOptions?: (inputText?: string) => void;
  updateValueSuggestions?: (fieldName: string, inputText?: string) => void;
  valueSuggestions?: ValueOption[];
}

export const QueryBuilder: React.FC<QueryBuilderProps> = ({
  externalQueryString = '',
  fieldOptions,
  getOperatorSuggestions,
  isError,
  isInitialized = true,
  isLoadingFieldOptions,
  isLoadingValueSuggestions,
  onChange,
  placeholder,
  prioritizedCloudFields,
  updateFieldOptions,
  updateValueSuggestions,
  valueSuggestions,
}) => {
  const editorKeyRef = useRef(0);

  const [popoverOpen, setPopoverOpen] = useState(false);

  const editorRef = useRef<HTMLDivElement>(null);
  const menuListRef = useRef<HTMLUListElement>(null);

  const editor = useSlateEditor();
  const { redo, resetHistory, saveSnapshot, undo } = useEditorHistory(editor);
  const { handleChange, lastSentValueRef, setValue, value, valueRef } = useEditorValue(
    externalQueryString,
    editor,
    onChange,
  );

  useEffect(() => {
    if (isInitialized) {
      resetHistory();
    }
  }, [isInitialized, resetHistory]);

  const { applySuggestion, handleKeyDown, suggestions, updateSuggestionContextAndFetch } =
    useSuggestions({
      editor,
      fieldOptions,
      getOperatorSuggestions,
      isLoadingFieldOptions,
      isLoadingValueSuggestions,
      menuListRef,
      prioritizedCloudFields,
      updateFieldOptions,
      updateValueSuggestions,
      valueSuggestions,
    });

  const { renderElement, renderLeaf } = useRenderers();
  const decorate = useDecorate();

  const editorLatestRef = useRef(editor);
  const setValueLatestRef = useRef(setValue);
  editorLatestRef.current = editor;
  setValueLatestRef.current = setValue;

  useEffect(() => {
    const handleWindowFocus = () => {
      editorKeyRef.current += 1;

      if (editorLatestRef.current.children.length === 0) {
        setValueLatestRef.current(valueRef.current);
      }
    };

    window.addEventListener('focus', handleWindowFocus);

    return () => {
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, [valueRef]);

  const triggerExternalOnChange = useCallback(
    (preSerialized?: string) => {
      const value =
        preSerialized !== undefined
          ? preSerialized
          : serializeQueryElements(valueRef.current as CustomElement[]);
      lastSentValueRef.current = value; // Track so we can skip echo
      onChange?.(value || undefined);
    },
    [onChange, valueRef, lastSentValueRef],
  );

  const handleChangeWithSuggestions = useCallback(
    (newValue: Descendant[]) => {
      handleChange(newValue as CustomElement[]);
      if (editor.selection) {
        updateSuggestionContextAndFetch();
      }
      // Only notify parent when filter is complete or cleared; keeps draft local and avoids parent doing premature api calls or URL syncs on every keystroke
      const serialized = serializeQueryElements(newValue as CustomElement[]);
      const trimmed = serialized?.trim() ?? '';
      if (trimmed === '' || isFilterComplete(serialized ?? '')) {
        saveSnapshot();
        triggerExternalOnChange();
      }
    },
    [handleChange, updateSuggestionContextAndFetch, editor, triggerExternalOnChange, saveSnapshot],
  );

  const handleSuggestionClick = useCallback(
    (suggestionType: string, suggestion: string) => {
      applySuggestion(editor, suggestionType, suggestion);
    },
    [applySuggestion, editor],
  );

  // [PURPOSE]: Handles the blur event for the editor - and triggers the external onChange event
  const handleBlur = useCallback(
    (event: React.FocusEvent<HTMLDivElement>, force?: boolean) => {
      // Use relatedTarget to see where focus is going *before* the timeout
      const relatedTarget = event.relatedTarget as HTMLElement | null;

      setTimeout(() => {
        const editorElement = editorRef.current;
        const menuElement = menuListRef.current;
        // Check active element *inside* timeout
        const currentActiveElement = document.activeElement;

        // Determine if focus is still within the component (editor or menu)
        const isFocusStillInside =
          (editorElement && editorElement.contains(currentActiveElement)) ||
          (menuElement && menuElement.contains(currentActiveElement)) ||
          // Also check if the relatedTarget (where focus went *during* the blur event)
          // points to the editor or menu, as activeElement might not update immediately
          (relatedTarget && editorElement?.contains(relatedTarget)) ||
          (relatedTarget && menuElement?.contains(relatedTarget));

        if (!isFocusStillInside || force) {
          // Only close popover and trigger change if focus has truly left
          setPopoverOpen(false);

          // Clear isEditing flags before normalization
          Editor.withoutNormalizing(editor, () => {
            const textNodes = Editor.nodes(editor, {
              match: (n) => Text.isText(n) && (n as CustomText).isEditing === true,
            });

            for (const [, nodePath] of textNodes) {
              if (Editor.hasPath(editor, nodePath)) {
                Transforms.unsetNodes(editor, 'isEditing', { at: nodePath });
              }
            }
          });

          // Safely blur the editor
          try {
            // Check if ReactEditor is still mounted and the editor has children
            if (
              editor.children.length > 0 &&
              editorElement &&
              ReactEditor.hasDOMNode(editor, editorElement) &&
              ReactEditor.isFocused(editor) // Only blur if Slate thinks it's focused
            ) {
              ReactEditor.blur(editor);
            }
          } catch {
            // Handle errors silently
          }

          fireImperitiveHandlers(editor);
          saveSnapshot();

          // Serialize from editor.children (post-normalization) and notify immediately
          const normalizedChildren = editor.children as CustomElement[];
          const serialized = serializeQueryElements(normalizedChildren);

          // Update internal state so Slate re-renders the chip
          valueRef.current = normalizedChildren;
          setValue([...normalizedChildren]); // New array reference forces React update

          // Notify parent (this will be tracked in lastSentValueRef to avoid echo overwrite)
          triggerExternalOnChange(serialized ?? '');
        }
      }, 0); // Use 0ms timeout to defer execution slightly after the event loop
    },
    [editor, saveSnapshot, valueRef, setValue, triggerExternalOnChange],
  );

  const handleFocus = useCallback(() => {
    // Check if editor is in valid state
    try {
      if (
        editor.children.length === 0 ||
        (!ReactEditor.isFocused(editor) && !menuListRef.current?.focus)
      ) {
        // Try to reinitialize editor if needed
        if (editor.children.length === 0) {
          setValue(valueRef.current);
        }
        // Safe focus
        try {
          ReactEditor.focus(editor);
        } catch {
          // Force remount on next tick if focus fails
          setTimeout(() => {
            editorKeyRef.current += 1;
            setValue(valueRef.current);
          }, 0);
        }
      }
    } catch {
      // Force editor reinit on error
      editorKeyRef.current += 1;
      setValue(valueRef.current);
    }

    setPopoverOpen(true);
    updateSuggestionContextAndFetch();
  }, [updateSuggestionContextAndFetch, setPopoverOpen, editor, setValue, valueRef]);

  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      Transforms.select(editor, {
        anchor: Editor.start(editor, []),
        focus: Editor.end(editor, []),
      });
      Transforms.delete(editor);
      handleBlur(e as unknown as React.FocusEvent<HTMLDivElement>, true);
    },
    [editor, handleBlur],
  );

  const applySnapshot = useCallback(
    (newChildren: CustomElement[] | undefined) => {
      if (newChildren) {
        setValue(newChildren);
        valueRef.current = newChildren;
        triggerExternalOnChange();
      }
    },
    [setValue, valueRef, triggerExternalOnChange],
  );

  const handleEditorKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (['Escape', 'Enter'].includes(event.key)) {
        event.preventDefault();
        handleBlur(event as unknown as React.FocusEvent<HTMLDivElement>, true);

        return;
      }

      // Snapshot-based undo/redo
      const isModKey = event.metaKey || event.ctrlKey;
      if (isModKey && event.key.toLowerCase() === 'z') {
        event.preventDefault();
        applySnapshot(event.shiftKey ? redo() : undo());

        return;
      }

      if (isModKey && event.key.toLowerCase() === 'y') {
        event.preventDefault();
        applySnapshot(redo());

        return;
      }

      if (!popoverOpen) {
        setPopoverOpen(true);
      }

      handleKeyDown(event);

      if (event.isDefaultPrevented()) {
        return;
      }
    },
    [handleKeyDown, setPopoverOpen, handleBlur, popoverOpen, undo, redo, applySnapshot],
  );

  const shouldRenderSuggestionMenu = useMemo(() => {
    return suggestions.length > 0 || isLoadingValueSuggestions;
  }, [suggestions, isLoadingValueSuggestions]);

  return (
    <Box
      onBlur={(event: React.FocusEvent<HTMLDivElement>) => handleBlur(event)}
      onFocus={handleFocus}
      sx={{ position: 'relative', width: '100%' }}
    >
      <EditorContainer editorRef={editorRef} handleClear={handleClear} isError={isError}>
        <Slate
          key="query-builder-editor"
          editor={editor}
          initialValue={value}
          onChange={handleChangeWithSuggestions}
        >
          <Editable
            aria-label="Filter bar with automatic chip creation"
            decorate={decorate}
            onKeyDown={handleEditorKeyDown}
            placeholder={placeholder ?? 'Search by resource name, tags or filters'}
            renderElement={renderElement}
            renderLeaf={renderLeaf}
            role="textbox"
            spellCheck={false}
            style={{
              minHeight: '24px',
              outline: 'none',
              overflowWrap: 'break-word',
              whiteSpace: 'pre-wrap',
              width: '100%',
              wordBreak: 'break-word',
            }}
          />
        </Slate>
      </EditorContainer>

      {shouldRenderSuggestionMenu && (
        <SuggestionMenu
          ref={menuListRef}
          anchorEl={editorRef.current}
          onClose={() => setPopoverOpen(false)}
          onSelect={handleSuggestionClick}
          open={popoverOpen}
          suggestions={suggestions}
        />
      )}
    </Box>
  );
};
